import type { JSX } from 'react';
import { useState, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { useTheme } from 'next-themes';
import { env } from '@/shared/config/env';
import type { Company } from '../types/company.types';

interface CompanyLogoProps {
  company: Company;
}

// Get backend base URL (without /api/v1) for static files
const BACKEND_BASE_URL = env.VITE_API_URL.replace(/\/api\/v1$/, '');

function getLogoUrl(logo: string): string {
  if (logo.startsWith('http://') || logo.startsWith('https://')) {
    return logo;
  }
  return `${BACKEND_BASE_URL}${logo}`;
}

export function CompanyLogo({ company }: CompanyLogoProps): JSX.Element {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  // After mounting, use theme-aware logo selection
  const isDarkMode = mounted && resolvedTheme === 'dark';

  // Select logo based on theme, with fallbacks
  const logoToShow =
    isDarkMode && company.darkLogo ? company.darkLogo : (company.logo ?? company.darkLogo);

  if (logoToShow) {
    return (
      <img
        src={getLogoUrl(logoToShow)}
        alt={`${company.name} logo`}
        className="h-12 w-auto max-w-32 rounded-md object-contain"
      />
    );
  }

  return (
    <div
      data-testid="company-logo-placeholder"
      className="flex h-12 w-12 items-center justify-center rounded-md bg-muted"
    >
      <Building2 className="h-6 w-6 text-muted-foreground" />
    </div>
  );
}
